// src/firebase.js - Firebase Email/Password Authentication
import { initializeApp } from 'firebase/app';
import {
  getAuth,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  sendPasswordResetEmail,
  sendEmailVerification,
  updateProfile,
  onAuthStateChanged,
  applyActionCode,
  checkActionCode,
  GoogleAuthProvider,
  signInWithPopup,
  signInWithCustomToken,
  fetchSignInMethodsForEmail
} from 'firebase/auth';
import { getFirestore, doc, setDoc, getDoc, updateDoc, deleteDoc, serverTimestamp, collection, query, where, getDocs, orderBy, limit, addDoc } from 'firebase/firestore';
import { getStorage } from 'firebase/storage';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { getAnalytics, logEvent } from 'firebase/analytics';

const firebaseConfig = {
  apiKey: "AIzaSyAGzUVclyR2d3OO2bhx1kol_Uf87HXQhD8",
  authDomain: "bluebook-exam.firebaseapp.com",
  projectId: "bluebook-exam",
  storageBucket: "bluebook-exam.firebasestorage.app",
  messagingSenderId: "520074133333",
  appId: "1:520074133333:web:2bcf425a9f9f36284439dc",
  measurementId: "G-XXXXXXXXXX" // Firebase Console에서 확인 필요
};

let app, auth, db, storage, functions, analytics;

try {
  app = initializeApp(firebaseConfig);
  auth = getAuth(app);
  db = getFirestore(app);
  storage = getStorage(app);
  functions = getFunctions(app, 'asia-northeast3');

  // Analytics 초기화 (브라우저 환경에서만)
  if (typeof window !== 'undefined') {
    analytics = getAnalytics(app);
    console.log('✅ Firebase Analytics 초기화 성공');
  }

  console.log('✅ Firebase 초기화 성공');
} catch (error) {
  console.log('⚠️ Firebase 초기화 실패:', error.message);
}

// Analytics 이벤트 로깅 헬퍼 함수
export const trackEvent = (eventName, eventParams = {}) => {
  if (analytics) {
    logEvent(analytics, eventName, eventParams);
    console.log('📊 Analytics Event:', eventName, eventParams);
  }
};

// 회원가입
export const signUp = async (email, password, displayName) => {
  try {
    console.log('📝 회원가입 시도...', email);

    // 1. Firebase Auth에 사용자 생성
    const userCredential = await createUserWithEmailAndPassword(auth, email, password);
    const user = userCredential.user;

    // 2. 프로필 업데이트 (이름 설정)
    await updateProfile(user, { displayName });

    // 3. 커스텀 SMTP로 한글 인증 이메일 발송
    try {
      const sendVerificationEmail = httpsCallable(functions, 'sendVerificationEmail');
      await sendVerificationEmail({
        userId: user.uid,
        email: user.email,
        displayName: displayName
      });
      console.log('📧 커스텀 인증 이메일 발송:', user.email);
    } catch (emailError) {
      console.error('⚠️ 커스텀 이메일 발송 실패, Firebase 기본 이메일 시도:', emailError);
      // 폴백: Firebase 기본 이메일 인증
      const actionCodeSettings = {
        url: 'https://bluebook-exam.web.app/?emailVerified=true',
        handleCodeInApp: true
      };
      await sendEmailVerification(user, actionCodeSettings);
      console.log('📧 Firebase 기본 인증 이메일 발송:', user.email);
    }

    // 4. Firestore에 사용자 정보 저장
    const now = new Date();

    await setDoc(doc(db, 'users', user.uid), {
      email: user.email,
      displayName: displayName,
      role: 'user', // 기본 역할
      createdAt: now.toISOString(),
      emailVerified: false,
      // 구독 없음 - 유료 구독 필요
      subscription: null,
      loginAttempts: {
        count: 0,
        lastAttempt: null,
        lockedUntil: null
      }
    });

    console.log('✅ 회원가입 성공:', user.email);
    return user;
  } catch (error) {
    console.error('❌ 회원가입 실패:', error);
    throw error;
  }
};

// 로그인
export const signIn = async (email, password) => {
  try {
    console.log('🔐 로그인 시도...', email);
    const userCredential = await signInWithEmailAndPassword(auth, email, password);
    console.log('✅ 로그인 성공:', userCredential.user.email);
    return userCredential.user;
  } catch (error) {
    console.error('❌ 로그인 실패:', error);
    throw error;
  }
};

// 로그아웃 (세션 정리 포함)
export const logOut = async () => {
  try {
    // 세션 토큰 제거
    const sessionToken = localStorage.getItem('bluebook_session_token');
    if (sessionToken) {
      try {
        // 세션 문서 삭제
        const sessionsRef = collection(db, 'sessions');
        const q = query(
          sessionsRef,
          where('sessionToken', '==', sessionToken)
        );
        const snapshot = await getDocs(q);

        if (!snapshot.empty) {
          const sessionRef = doc(db, 'sessions', snapshot.docs[0].id);
          await updateDoc(sessionRef, { isActive: false });
          console.log('🔒 세션 비활성화 완료');
        }
      } catch (sessionError) {
        console.warn('⚠️ 세션 정리 중 오류 (무시):', sessionError);
      }
      localStorage.removeItem('bluebook_session_token');
    }

    await signOut(auth);
    console.log('✅ 로그아웃 성공');
    return true;
  } catch (error) {
    console.error('❌ 로그아웃 실패:', error);
    return false;
  }
};

// 이메일로 사용자의 인증 제공자(로그인 방법) 확인
export const getAuthProviders = async (email) => {
  try {
    const methods = await fetchSignInMethodsForEmail(auth, email);
    return methods; // ['google.com'], ['password'], 또는 ['google.com', 'password'] 등
  } catch (error) {
    console.error('인증 제공자 확인 실패:', error);
    return [];
  }
};

// 비밀번호 재설정 이메일 전송 (커스텀 SMTP 한글 이메일)
export const resetPassword = async (email) => {
  try {
    // 커스텀 SMTP로 한글 비밀번호 재설정 이메일 발송
    try {
      const sendPasswordResetEmailCustom = httpsCallable(functions, 'sendPasswordResetEmailCustom');
      await sendPasswordResetEmailCustom({ email });
      console.log('✅ 커스텀 비밀번호 재설정 이메일 전송:', email);
      return true;
    } catch (customError) {
      console.error('⚠️ 커스텀 이메일 발송 실패, Firebase 기본 이메일 시도:', customError);
      // 폴백: Firebase 기본 비밀번호 재설정 이메일
      await sendPasswordResetEmail(auth, email);
      console.log('✅ Firebase 기본 비밀번호 재설정 이메일 전송:', email);
      return true;
    }
  } catch (error) {
    console.error('❌ 비밀번호 재설정 실패:', error);
    throw error;
  }
};

// 사용자 역할 확인
export const getUserRole = async (uid) => {
  try {
    const userDoc = await getDoc(doc(db, 'users', uid));
    if (userDoc.exists()) {
      return userDoc.data().role || 'user';
    }
    return 'user';
  } catch (error) {
    console.error('❌ 역할 확인 실패:', error);
    return 'user';
  }
};

// 로그인 시도 횟수 확인 및 계정 잠금 체크
// localStorage 기반으로 클라이언트에서 관리
export const checkLoginAttempts = async (email) => {
  try {
    const MAX_ATTEMPTS = 5;
    const LOCK_DURATION_MINUTES = 30;

    // localStorage에서 로그인 시도 정보 가져오기
    const storageKey = `loginAttempts_${email}`;
    const attemptsData = localStorage.getItem(storageKey);

    if (!attemptsData) {
      // 첫 로그인 시도
      return { allowed: true, attemptsRemaining: MAX_ATTEMPTS };
    }

    const attempts = JSON.parse(attemptsData);
    const now = new Date().getTime();

    // 잠금 시간이 지났는지 확인
    if (attempts.lockedUntil && now < attempts.lockedUntil) {
      const remainingMinutes = Math.ceil((attempts.lockedUntil - now) / (1000 * 60));
      console.log(`🔒 계정 잠김 (${remainingMinutes}분 남음)`);
      return {
        allowed: false,
        locked: true,
        remainingMinutes,
        attemptsRemaining: 0
      };
    }

    // 잠금 시간이 지났으면 초기화
    if (attempts.lockedUntil && now >= attempts.lockedUntil) {
      localStorage.removeItem(storageKey);
      return { allowed: true, attemptsRemaining: MAX_ATTEMPTS };
    }

    // 시도 횟수 확인
    const attemptsRemaining = MAX_ATTEMPTS - attempts.count;

    if (attempts.count >= MAX_ATTEMPTS) {
      // 최대 시도 횟수 초과 - 계정 잠금
      const lockedUntil = now + (LOCK_DURATION_MINUTES * 60 * 1000);
      localStorage.setItem(storageKey, JSON.stringify({
        ...attempts,
        lockedUntil
      }));

      console.log(`🔒 계정 잠김 (${LOCK_DURATION_MINUTES}분)`);
      return {
        allowed: false,
        locked: true,
        remainingMinutes: LOCK_DURATION_MINUTES,
        attemptsRemaining: 0
      };
    }

    console.log(`✅ 로그인 가능 (남은 시도: ${attemptsRemaining}회)`);
    return { allowed: true, attemptsRemaining };

  } catch (error) {
    console.error('❌ 로그인 시도 확인 실패:', error);
    return { allowed: true, attemptsRemaining: 5 };
  }
};

// 로그인 실패 기록
// localStorage 기반으로 클라이언트에서 관리
export const recordLoginFailure = async (email) => {
  try {
    const MAX_ATTEMPTS = 5;
    const LOCK_DURATION_MINUTES = 30;

    // localStorage에서 로그인 시도 정보 가져오기
    const storageKey = `loginAttempts_${email}`;
    const attemptsData = localStorage.getItem(storageKey);

    let attempts;
    if (!attemptsData) {
      // 첫 실패
      attempts = {
        count: 1,
        lastAttempt: new Date().getTime()
      };
    } else {
      attempts = JSON.parse(attemptsData);
      attempts.count += 1;
      attempts.lastAttempt = new Date().getTime();
    }

    // 최대 시도 횟수 도달 시 계정 잠금
    if (attempts.count >= MAX_ATTEMPTS) {
      attempts.lockedUntil = new Date().getTime() + (LOCK_DURATION_MINUTES * 60 * 1000);
      localStorage.setItem(storageKey, JSON.stringify(attempts));

      console.log(`🔒 계정 잠김 (${MAX_ATTEMPTS}회 실패)`);
      return {
        count: attempts.count,
        locked: true,
        attemptsRemaining: 0,
        remainingMinutes: LOCK_DURATION_MINUTES
      };
    }

    localStorage.setItem(storageKey, JSON.stringify(attempts));

    const attemptsRemaining = MAX_ATTEMPTS - attempts.count;
    console.log(`⚠️ 로그인 실패 (${attempts.count}/${MAX_ATTEMPTS}회, 남은 시도: ${attemptsRemaining}회)`);

    return {
      count: attempts.count,
      locked: false,
      attemptsRemaining
    };

  } catch (error) {
    console.error('❌ 로그인 실패 기록 실패:', error);
    return { count: 1, locked: false, attemptsRemaining: 4 };
  }
};

// 로그인 성공 시 시도 횟수 초기화
export const resetLoginAttempts = async (email) => {
  try {
    // localStorage에서 해당 이메일의 로그인 시도 기록 삭제
    const storageKey = `loginAttempts_${email}`;
    localStorage.removeItem(storageKey);
    console.log('✅ 로그인 시도 초기화 완료');
  } catch (error) {
    console.error('❌ 로그인 시도 초기화 실패:', error);
  }
};

// 계정 잠금 해제 (이메일 링크 클릭 시)
// localStorage 기반으로 클라이언트에서 관리
export const unlockAccount = async (email) => {
  try {
    // localStorage에서 해당 이메일의 로그인 시도 기록 삭제
    const storageKey = `loginAttempts_${email}`;
    localStorage.removeItem(storageKey);
    console.log('✅ 계정 잠금 해제 완료:', email);
    return true;
  } catch (error) {
    console.error('❌ 계정 잠금 해제 실패:', error);
    throw error;
  }
};

// 이메일 인증 메일 재전송 (커스텀 SMTP 한글 이메일)
export const resendVerificationEmail = async () => {
  try {
    const user = auth.currentUser;
    if (!user) {
      throw new Error('로그인이 필요합니다.');
    }

    // 커스텀 SMTP로 한글 인증 이메일 재발송
    try {
      const resendVerificationEmailFn = httpsCallable(functions, 'resendVerificationEmail');
      await resendVerificationEmailFn({
        userId: user.uid,
        email: user.email,
        displayName: user.displayName || '회원'
      });
      console.log('📧 커스텀 인증 이메일 재전송:', user.email);
      return true;
    } catch (customError) {
      console.error('⚠️ 커스텀 이메일 재발송 실패, Firebase 기본 이메일 시도:', customError);
      // 폴백: Firebase 기본 이메일 인증
      const actionCodeSettings = {
        url: 'https://bluebook-exam.web.app/?emailVerified=true',
        handleCodeInApp: true
      };
      await sendEmailVerification(user, actionCodeSettings);
      console.log('📧 Firebase 기본 인증 이메일 재전송:', user.email);
      return true;
    }
  } catch (error) {
    console.error('❌ 인증 이메일 재전송 실패:', error);
    throw error;
  }
};

// 이메일 인증 처리 (Firebase 기본 액션 코드용)
export const verifyEmailWithCode = async (actionCode) => {
  try {
    console.log('📧 이메일 인증 코드 확인 중...');

    // 1. 액션 코드 정보 확인
    const info = await checkActionCode(auth, actionCode);
    console.log('✅ 액션 코드 확인 완료:', info);

    // 2. 이메일 인증 적용
    await applyActionCode(auth, actionCode);
    console.log('✅ 이메일 인증 완료');

    // 3. 현재 사용자가 있으면 Firestore 업데이트
    const user = auth.currentUser;
    if (user) {
      await user.reload(); // 사용자 정보 새로고침
      await updateDoc(doc(db, 'users', user.uid), {
        emailVerified: true
      });
      console.log('✅ Firestore 이메일 인증 상태 업데이트');
    }

    return { success: true, user };
  } catch (error) {
    console.error('❌ 이메일 인증 실패:', error);
    let errorMessage = '이메일 인증에 실패했습니다.';

    if (error.code === 'auth/invalid-action-code') {
      errorMessage = '인증 링크가 만료되었거나 이미 사용되었습니다.';
    } else if (error.code === 'auth/expired-action-code') {
      errorMessage = '인증 링크가 만료되었습니다. 새로운 인증 메일을 요청해주세요.';
    }

    return { success: false, error: errorMessage };
  }
};

// 커스텀 토큰으로 이메일 인증 처리 (커스텀 SMTP 이메일용)
export const verifyEmailWithToken = async (token) => {
  try {
    console.log('📧 커스텀 토큰으로 이메일 인증 중...');

    const verifyEmailFn = httpsCallable(functions, 'verifyEmail');
    const result = await verifyEmailFn({ token });

    if (result.data.success) {
      console.log('✅ 이메일 인증 완료:', result.data.email);
      return { success: true, email: result.data.email };
    } else {
      return { success: false, error: result.data.message || '인증에 실패했습니다.' };
    }
  } catch (error) {
    console.error('❌ 이메일 인증 실패:', error);
    const errorMessage = error.message || '이메일 인증에 실패했습니다.';
    return { success: false, error: errorMessage };
  }
};

// 비밀번호 재설정 토큰 검증 (커스텀 SMTP 이메일용)
export const verifyPasswordResetToken = async (token) => {
  try {
    console.log('🔐 비밀번호 재설정 토큰 검증 중...');

    const verifyTokenFn = httpsCallable(functions, 'verifyPasswordResetToken');
    const result = await verifyTokenFn({ token });

    if (result.data.valid) {
      console.log('✅ 토큰 유효:', result.data.email);
      return { valid: true, email: result.data.email };
    } else {
      return { valid: false, error: result.data.error || '유효하지 않은 토큰입니다.' };
    }
  } catch (error) {
    console.error('❌ 토큰 검증 실패:', error);
    return { valid: false, error: error.message || '토큰 검증에 실패했습니다.' };
  }
};

// 비밀번호 재설정 완료 (커스텀 SMTP 이메일용)
export const completePasswordReset = async (token, newPassword) => {
  try {
    console.log('🔐 비밀번호 재설정 처리 중...');

    const completeResetFn = httpsCallable(functions, 'completePasswordReset');
    const result = await completeResetFn({ token, newPassword });

    if (result.data.success) {
      console.log('✅ 비밀번호 재설정 완료');
      return { success: true, message: result.data.message };
    } else {
      return { success: false, error: result.data.message || '비밀번호 변경에 실패했습니다.' };
    }
  } catch (error) {
    console.error('❌ 비밀번호 재설정 실패:', error);
    return { success: false, error: error.message || '비밀번호 변경에 실패했습니다.' };
  }
};

// 하위 호환성을 위한 별칭 (기존 코드에서 사용 중)
export const mockSignIn = signIn;
export const mockSignOut = logOut;

export const testFirebaseConnection = async () => {
  try {
    const response = await fetch(
      'https://asia-northeast3-bluebook-exam.cloudfunctions.net/healthCheck'
    );
    return { success: response.ok, data: await response.json() };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// ============================================
// 세션 관리 (동시 2대 기기 제한)
// ============================================

const MAX_SESSIONS = 2; // 최대 동시 로그인 기기 수
const SESSION_TOKEN_KEY = 'bluebook_session_token';

// 고유 세션 토큰 생성
const generateSessionToken = () => {
  return 'sess_' + Date.now() + '_' + Math.random().toString(36).substring(2, 15);
};

// 디바이스 정보 수집
const getDeviceInfo = () => {
  const ua = navigator.userAgent;
  let deviceType = 'Unknown';
  let browser = 'Unknown';
  let os = 'Unknown';

  // OS 감지
  if (ua.includes('Windows')) os = 'Windows';
  else if (ua.includes('Mac')) os = 'macOS';
  else if (ua.includes('Linux')) os = 'Linux';
  else if (ua.includes('Android')) os = 'Android';
  else if (ua.includes('iPhone') || ua.includes('iPad')) os = 'iOS';

  // 브라우저 감지
  if (ua.includes('Chrome') && !ua.includes('Edg')) browser = 'Chrome';
  else if (ua.includes('Safari') && !ua.includes('Chrome')) browser = 'Safari';
  else if (ua.includes('Firefox')) browser = 'Firefox';
  else if (ua.includes('Edg')) browser = 'Edge';

  // 디바이스 타입
  if (ua.includes('Mobile') || ua.includes('Android') || ua.includes('iPhone')) {
    deviceType = 'Mobile';
  } else if (ua.includes('iPad') || ua.includes('Tablet')) {
    deviceType = 'Tablet';
  } else {
    deviceType = 'Desktop';
  }

  return `${deviceType} - ${os} (${browser})`;
};

// 1. 세션 생성
export const createSession = async (userId) => {
  try {
    const sessionToken = generateSessionToken();
    const deviceInfo = getDeviceInfo();
    const now = new Date();

    const sessionData = {
      userId,
      sessionToken,
      deviceInfo,
      createdAt: now.toISOString(),
      lastActiveAt: now.toISOString(),
      isActive: true
    };

    // Firestore에 세션 추가
    const sessionsRef = collection(db, 'sessions');
    const docRef = await addDoc(sessionsRef, sessionData);

    // localStorage에 세션 토큰 저장
    localStorage.setItem(SESSION_TOKEN_KEY, sessionToken);

    console.log('✅ 세션 생성 완료:', docRef.id, deviceInfo);
    return { sessionId: docRef.id, sessionToken };
  } catch (error) {
    console.error('❌ 세션 생성 실패:', error);
    throw error;
  }
};

// 2. 활성 세션 조회
export const getActiveSessions = async (userId) => {
  try {
    const sessionsRef = collection(db, 'sessions');
    const q = query(
      sessionsRef,
      where('userId', '==', userId),
      where('isActive', '==', true),
      orderBy('createdAt', 'asc')
    );

    const snapshot = await getDocs(q);
    const sessions = [];

    snapshot.forEach((doc) => {
      sessions.push({ id: doc.id, ...doc.data() });
    });

    console.log(`📱 활성 세션 ${sessions.length}개 발견`);
    return sessions;
  } catch (error) {
    console.error('❌ 세션 조회 실패:', error);
    return [];
  }
};

// 3. 세션 검증 및 관리 (2대 초과 시 가장 오래된 세션 비활성화)
export const validateAndManageSessions = async (userId, currentSessionToken) => {
  try {
    const sessions = await getActiveSessions(userId);

    // 현재 세션이 목록에 있는지 확인
    const currentSessionExists = sessions.some(s => s.sessionToken === currentSessionToken);

    if (sessions.length <= MAX_SESSIONS) {
      // 2대 이하면 허용
      return { allowed: true, kickedSession: null };
    }

    // 2대 초과 - 가장 오래된 세션 비활성화
    // 현재 세션은 유지하고 나머지 중 가장 오래된 것 제거
    const sessionsToCheck = currentSessionExists
      ? sessions.filter(s => s.sessionToken !== currentSessionToken)
      : sessions;

    if (sessionsToCheck.length > 0) {
      // 가장 오래된 세션 (이미 createdAt 오름차순 정렬됨)
      const oldestSession = sessionsToCheck[0];
      await deactivateSession(oldestSession.id);

      console.log('🔒 오래된 세션 비활성화:', oldestSession.id, oldestSession.deviceInfo);
      return { allowed: true, kickedSession: oldestSession.id };
    }

    return { allowed: true, kickedSession: null };
  } catch (error) {
    console.error('❌ 세션 검증 실패:', error);
    // 오류 시에도 로그인 허용 (사용자 경험 우선)
    return { allowed: true, kickedSession: null };
  }
};

// 4. 세션 비활성화
export const deactivateSession = async (sessionId) => {
  try {
    const sessionRef = doc(db, 'sessions', sessionId);
    await updateDoc(sessionRef, {
      isActive: false,
      deactivatedAt: new Date().toISOString()
    });
    console.log('🔒 세션 비활성화 완료:', sessionId);
    return true;
  } catch (error) {
    console.error('❌ 세션 비활성화 실패:', error);
    return false;
  }
};

// 5. 현재 세션 유효성 확인
export const checkCurrentSession = async (userId, sessionToken) => {
  try {
    if (!sessionToken) {
      return { valid: false, reason: 'no_token' };
    }

    const sessionsRef = collection(db, 'sessions');
    const q = query(
      sessionsRef,
      where('userId', '==', userId),
      where('sessionToken', '==', sessionToken),
      limit(1)
    );

    const snapshot = await getDocs(q);

    if (snapshot.empty) {
      return { valid: false, reason: 'session_not_found' };
    }

    const sessionData = snapshot.docs[0].data();

    if (!sessionData.isActive) {
      return { valid: false, reason: 'session_deactivated' };
    }

    // 세션 활동 시간 업데이트
    const sessionRef = doc(db, 'sessions', snapshot.docs[0].id);
    await updateDoc(sessionRef, {
      lastActiveAt: new Date().toISOString()
    });

    return { valid: true, reason: 'active' };
  } catch (error) {
    console.error('❌ 세션 확인 실패:', error);
    // 오류 시에도 유효한 것으로 처리 (네트워크 오류 등)
    return { valid: true, reason: 'error_fallback' };
  }
};

// 6. 세션 제거 (로그아웃 시)
export const removeSession = async (sessionToken) => {
  try {
    if (!sessionToken) return false;

    const sessionsRef = collection(db, 'sessions');
    const q = query(
      sessionsRef,
      where('sessionToken', '==', sessionToken),
      limit(1)
    );

    const snapshot = await getDocs(q);

    if (!snapshot.empty) {
      const sessionRef = doc(db, 'sessions', snapshot.docs[0].id);
      await deleteDoc(sessionRef);
      console.log('🗑️ 세션 삭제 완료:', sessionToken);
    }

    localStorage.removeItem(SESSION_TOKEN_KEY);
    return true;
  } catch (error) {
    console.error('❌ 세션 삭제 실패:', error);
    localStorage.removeItem(SESSION_TOKEN_KEY);
    return false;
  }
};

// 7. 현재 저장된 세션 토큰 가져오기
export const getStoredSessionToken = () => {
  return localStorage.getItem(SESSION_TOKEN_KEY);
};

// 8. 특정 세션 강제 로그아웃 (다른 기기)
export const forceLogoutSession = async (sessionId) => {
  try {
    await deactivateSession(sessionId);
    return true;
  } catch (error) {
    console.error('❌ 강제 로그아웃 실패:', error);
    return false;
  }
};

// ============================================
// 소셜 로그인 (Google, Kakao, Naver)
// ============================================

// Google 로그인
export const signInWithGoogle = async () => {
  try {
    console.log('🔐 Google 로그인 시도...');

    const provider = new GoogleAuthProvider();
    provider.setCustomParameters({
      prompt: 'select_account' // 매번 계정 선택 화면 표시
    });

    const result = await signInWithPopup(auth, provider);
    const user = result.user;

    console.log('✅ Google 인증 성공:', user.email);

    // Firestore에 사용자 문서 확인/생성
    const userDoc = await getDoc(doc(db, 'users', user.uid));

    if (!userDoc.exists()) {
      // 신규 사용자 - Firestore에 정보 저장
      console.log('📝 신규 Google 사용자 등록...');

      const now = new Date();
      const trialEndDate = new Date(now.getTime() + 3 * 24 * 60 * 60 * 1000); // 3일 무료 체험
      const bonusEndDate = new Date(now.getTime() + 30 * 24 * 60 * 60 * 1000); // 30일 가입 보너스

      await setDoc(doc(db, 'users', user.uid), {
        email: user.email,
        displayName: user.displayName || user.email.split('@')[0],
        photoURL: user.photoURL || null,
        role: 'user',
        createdAt: now.toISOString(),
        emailVerified: true, // 소셜 로그인은 이미 인증됨
        authProvider: 'google',
        // 3일 무료 체험
        trial: {
          isActive: true,
          startDate: now.toISOString(),
          endDate: trialEndDate.toISOString(),
          onlineTestUnlimited: true,
          pdfDownloads: 3,
          pdfDownloadsUsed: 0
        },
        // 30일 가입 보너스 (PDF 다운로드 10회)
        subscription: {
          plan: "signup_bonus",
          status: "active",
          downloadsRemaining: 10,
          startDate: now.toISOString(),
          endDate: bonusEndDate.toISOString(),
          isBonus: true,
          sectionLimits: {
            vocabulary: 2,
            grammar: 2,
            bluebook_original: 2
          },
          notificationsSent: {
            sevenDays: false,
            threeDays: false,
            oneDay: false,
            expired: false
          }
        },
        loginAttempts: {
          count: 0,
          lastAttempt: null,
          lockedUntil: null
        }
      });

      console.log('✅ 신규 Google 사용자 등록 완료');
    } else {
      // 기존 사용자 - authProvider 업데이트 (처음 Google로 로그인한 경우)
      const userData = userDoc.data();
      if (!userData.authProvider) {
        await updateDoc(doc(db, 'users', user.uid), {
          authProvider: 'google',
          photoURL: user.photoURL || userData.photoURL || null
        });
      }
    }

    return { success: true, user, isNewUser: !userDoc.exists() };
  } catch (error) {
    console.error('❌ Google 로그인 실패:', error);

    let errorMessage = 'Google 로그인에 실패했습니다.';
    if (error.code === 'auth/popup-closed-by-user') {
      errorMessage = '로그인 창이 닫혔습니다. 다시 시도해주세요.';
    } else if (error.code === 'auth/popup-blocked') {
      errorMessage = '팝업이 차단되었습니다. 팝업 차단을 해제해주세요.';
    } else if (error.code === 'auth/cancelled-popup-request') {
      errorMessage = '로그인이 취소되었습니다.';
    } else if (error.code === 'auth/account-exists-with-different-credential') {
      errorMessage = '이미 다른 방식으로 가입된 이메일입니다.';
    }

    return { success: false, error: errorMessage };
  }
};

// Kakao 로그인 (Kakao SDK 2.7.2 버전 - authorize 사용)
export const signInWithKakao = async () => {
  try {
    console.log('🔐 Kakao 로그인 시도...');

    // Kakao SDK 초기화 확인
    if (!window.Kakao) {
      throw new Error('Kakao SDK가 로드되지 않았습니다.');
    }

    if (!window.Kakao.isInitialized()) {
      throw new Error('Kakao SDK가 초기화되지 않았습니다.');
    }

    // 현재 URL을 리다이렉트 URI로 사용
    const currentUrl = window.location.origin + window.location.pathname;

    // 카카오 로그인 페이지로 리다이렉트 (팝업 대신)
    window.Kakao.Auth.authorize({
      redirectUri: currentUrl,
      state: 'kakao_login'
    });

    // authorize는 페이지를 리다이렉트하므로 여기서 반환하지 않음
    return { success: true, message: '카카오 로그인 페이지로 이동합니다.' };
  } catch (error) {
    console.error('❌ Kakao 로그인 실패:', error);
    return { success: false, error: error.message || 'Kakao 로그인에 실패했습니다.' };
  }
};

// Kakao 로그인 콜백 처리 (리다이렉트 후 호출)
export const handleKakaoCallback = async () => {
  try {
    // URL에서 인증 코드 추출
    const urlParams = new URLSearchParams(window.location.search);
    const code = urlParams.get('code');
    const state = urlParams.get('state');

    if (!code || state !== 'kakao_login') {
      return null; // 카카오 콜백이 아님
    }

    console.log('🔐 Kakao 콜백 처리 중... code:', code);

    // URL에서 code 파라미터 제거 (히스토리 정리)
    const cleanUrl = window.location.origin + window.location.pathname;
    window.history.replaceState({}, document.title, cleanUrl);

    // Cloud Function 호출하여 Custom Token 획득
    const kakaoLoginFn = httpsCallable(functions, 'kakaoLogin');
    const result = await kakaoLoginFn({
      kakaoAuthCode: code,
      redirectUri: cleanUrl
    });

    if (!result.data.success) {
      throw new Error(result.data.error || 'Kakao 로그인 처리 실패');
    }

    // Firebase Custom Token으로 로그인
    await signInWithCustomToken(auth, result.data.customToken);

    console.log('✅ Kakao 로그인 완료');
    return {
      success: true,
      user: result.data.user,
      isNewUser: result.data.isNewUser
    };
  } catch (error) {
    console.error('❌ Kakao 콜백 처리 실패:', error);
    return { success: false, error: error.message };
  }
};

// Naver 로그인 SDK 초기화
export const initNaverLogin = (callbackUrl) => {
  if (!window.naver) {
    console.error('Naver SDK가 로드되지 않았습니다.');
    return null;
  }

  const naverLogin = new window.naver.LoginWithNaverId({
    clientId: process.env.REACT_APP_NAVER_CLIENT_ID,
    callbackUrl: callbackUrl || window.location.origin + '/auth/naver/callback',
    isPopup: false,
    callbackHandle: true
  });

  naverLogin.init();
  return naverLogin;
};

// Naver 로그인 (OAuth code 또는 access token 사용)
export const signInWithNaver = async (codeOrToken) => {
  try {
    console.log('🔐 Naver 로그인 시도...');

    // Cloud Function 호출하여 Custom Token 획득
    const naverLoginFn = httpsCallable(functions, 'naverLogin');
    // code인지 accessToken인지 판별 (code는 짧고, accessToken은 길다)
    const isCode = codeOrToken.length < 100;
    const result = await naverLoginFn(
      isCode ? { code: codeOrToken } : { naverAccessToken: codeOrToken }
    );

    if (!result.data.success) {
      throw new Error(result.data.error || 'Naver 로그인 처리 실패');
    }

    // Firebase Custom Token으로 로그인
    await signInWithCustomToken(auth, result.data.customToken);

    console.log('✅ Naver 로그인 완료');
    return {
      success: true,
      user: result.data.user,
      isNewUser: result.data.isNewUser
    };
  } catch (error) {
    console.error('❌ Naver 로그인 실패:', error);
    return { success: false, error: error.message || 'Naver 로그인에 실패했습니다.' };
  }
};

export { app, auth, db, storage, functions, onAuthStateChanged };
export default app;